import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addItemToCart, addToCart, getCart } from "../../actions/cartActions";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import PaymentModal from "../PaymentModal/PaymentModal";
import { normalizeMainProductForPayment } from "../../utils/productPaymentAdapters";

const BuyingBox = ({ productId, stock, item }) => {
  const dispatch = useDispatch();
  const { cart, loadingCartItem } = useSelector((s) => s.cartReducer);
  const { token } = useSelector((s) => s.authReducer);

  const isAuthed = Boolean(token && token !== "undefined");
  const userId = useMemo(() => {
    try { return isAuthed ? JSON.parse(token)?.id ?? null : null; }
    catch { return null; }
  }, [isAuthed, token]);

  const [quantity, setQuantity] = useState(1);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    if (userId) dispatch(getCart(userId));
  }, [dispatch, userId]);

  const addCart = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!isAuthed || !userId) {
      setErrorMessage("Please register or log in to add items to your cart.");
      return;
    }
    if (quantity < 1) {
      setErrorMessage("Quantity must be at least 1.");
      return;
    }
    if (quantity > (stock ?? 0)) {
      setErrorMessage("Quantity exceeds available stock.");
      return;
    }
    if (!cart?.id) {
      setErrorMessage("Cart not initialized yet. Please try again.");
      return;
    }

    try {
      setBusy(true);
      // 1) increment cart quantity
      const addRes = await dispatch(addToCart(userId, cart?.quantity ?? 0));
      if (!addRes?.ok) throw new Error(addRes?.error || "Failed to update cart");

      // 2) add/update the cart item row
      const lineRes = await dispatch(addItemToCart(cart.id, productId, quantity));
      if (!lineRes?.ok) throw new Error(lineRes?.error || "Failed to add item");

      // 3) fetch fresh cart to update Navbar badge immediately
      await dispatch(getCart(userId));

      setSuccessMessage(`${quantity} item(s) added to your cart.`);
    } catch (e) {
      setErrorMessage(e.message || "Unable to add to cart.");
    } finally {
      setBusy(false);
    }
  };

  const paymentProduct = useMemo(() => normalizeMainProductForPayment(item || { id: productId }, quantity), [item, productId, quantity]);

  return (
    <div className="my-4" style={{ boxShadow: "0 4px 8px rgba(0,0,0,0.2)", padding: 15 }}>
      <div className="mb-3">
        <div className="mb-2"><span>Stock: {stock ?? 0}</span></div>
        <span>Qty:</span>
        <Form.Control
          type="number"
          name="qty"
          max={stock ?? 0}
          min={1}
          value={quantity}
          onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
        />
      </div>

      {loadingCartItem ? (
        <div>Loading...</div>
      ) : (
        <>
          <Button
            onClick={addCart}
            variant="primary"
            className="me-2 mb-2"
            disabled={busy}
          >
            {busy ? (<><Spinner size="sm" animation="border" className="me-2" />Adding…</>) : "Add to Cart"}
          </Button>

          <Button
            onClick={() => setShowPaymentModal(true)}
            variant="primary"
          >
            Buy Now
          </Button>
        </>
      )}

      {errorMessage && <Alert variant="danger" className="mt-3">{errorMessage}</Alert>}
      {successMessage && <Alert variant="success" className="mt-3">{successMessage}</Alert>}
      <PaymentModal
        show={showPaymentModal}
        onHide={() => setShowPaymentModal(false)}
        product={paymentProduct}
      />
    </div>
  );
};

export default BuyingBox;
